import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import './CustomerDetail.css';

// Phone number formatting utility
const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');

  // Format as XXX-XXX-XXXX
  if (phoneNumber.length <= 3) {
    return phoneNumber;
  } else if (phoneNumber.length <= 6) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  } else {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
};

const buildAddressString = (street, city, state, zip) => {
  return [street, city, state, zip].filter(Boolean).join(', ');
};

const copyAddress = (text) => {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => {});
  }
};

const openInMaps = (text) => {
  window.open(`https://maps.google.com/?q=${encodeURIComponent(text)}`, '_blank');
};

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [vehicleForm, setVehicleForm] = useState({
    year: '', make: '', model: '', color: '',
    license_plate: '', vin: '', vehicle_type: 'car', notes: ''
  });
  const [editForm, setEditForm] = useState({});
  const [showAddReferral, setShowAddReferral] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    name: '', address: '', city: '', state: 'TX', zip: '', is_default: false
  });

  useEffect(() => {
    fetchCustomer();
    fetchInvoices();
    fetchAllCustomers();
  }, [id]);

  const fetchAllCustomers = async () => {
    try {
      const response = await fetch('/api/customers?limit=1000');
      const data = await response.json();
      setAllCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await fetch(`/api/invoices?customer_id=${id}`);
      const data = await response.json();
      // Sort by created_at descending (newest first)
      const sorted = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setInvoices(sorted);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const fetchCustomer = async () => {
    try {
      const response = await fetch(`/api/customers/${id}`);
      if (!response.ok) throw new Error('Customer not found');
      const data = await response.json();
      setCustomer(data);
      setEditForm(data);
    } catch (error) {
      console.error('Error:', error);
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/vehicles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...vehicleForm, customer_id: id })
      });

      if (response.ok) {
        setShowVehicleModal(false);
        setVehicleForm({
          year: '', make: '', model: '', color: '',
          license_plate: '', vin: '', vehicle_type: 'car', notes: ''
        });
        fetchCustomer();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`/api/customers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        setShowEditModal(false);
        fetchCustomer();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!window.confirm('Move this customer to trash? Their appointments and invoices will also be moved. You can restore them from the Trash page.')) {
      return;
    }

    try {
      const response = await fetch(`/api/customers/${id}`, { method: 'DELETE' });
      if (response.ok) {
        navigate('/customers');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const url = editingAddress
        ? `/api/customers/${id}/addresses/${editingAddress.id}`
        : `/api/customers/${id}/addresses`;
      const method = editingAddress ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addressForm)
      });

      if (response.ok) {
        setShowAddressModal(false);
        setEditingAddress(null);
        setAddressForm({ name: '', address: '', city: '', state: 'TX', zip: '', is_default: false });
        fetchCustomer();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressForm({
      name: address.name,
      address: address.address,
      city: address.city || '',
      state: address.state || 'TX',
      zip: address.zip || '',
      is_default: address.is_default === 1
    });
    setShowAddressModal(true);
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      const response = await fetch(`/api/customers/${id}/addresses/${addressId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        fetchCustomer();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      const response = await fetch(`/api/customers/${id}/addresses/${addressId}/default`, {
        method: 'PATCH'
      });
      if (response.ok) {
        fetchCustomer();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAddReferral = async (referredCustomerId) => {
    if (!referredCustomerId) return;
    try {
      const response = await fetch(`/api/customers/${referredCustomerId}/referred-by`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referred_by_customer_id: id })
      });
      if (response.ok) {
        setShowAddReferral(false);
        fetchCustomer();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="customer-detail">
      <div className="customer-detail__header">
        <div>
          <button className="btn btn--secondary btn--small mb-sm" onClick={() => navigate('/customers')}>
            &larr; Back to Customers
          </button>
          <h1 className="customer-detail__name">
            {customer.first_name} {customer.last_name}
          </h1>
        </div>
        <div className="customer-detail__actions">
          <button className="btn btn--secondary" onClick={() => setShowEditModal(true)}>
            Edit
          </button>
          <button className="btn btn--danger" onClick={handleDeleteCustomer}>
            Delete
          </button>
        </div>
      </div>

      <div className="customer-detail__grid">
        <div className="card">
          <h3 className="card__title">Contact Information</h3>
          <div className="info-list">
            <div className="info-item">
              <span className="info-label">Phone</span>
              <span className="info-value">{customer.phone}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Email</span>
              <span className="info-value">{customer.email || '-'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Contact Preference</span>
              <span className="info-value">
                {customer.contact_preference === 'sms' && 'SMS Only'}
                {customer.contact_preference === 'email' && 'Email Only'}
                {customer.contact_preference === 'both' && 'SMS & Email'}
                {customer.contact_preference === 'none' && 'Do Not Contact'}
                {!customer.contact_preference && 'SMS & Email'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Address</span>
              <span className="info-value">
                {customer.address ? (
                  <>
                    {customer.address}<br />
                    {customer.city}, {customer.state} {customer.zip}
                    <span style={{ display: 'inline-flex', gap: '6px', marginLeft: '8px', verticalAlign: 'middle' }}>
                      <button
                        className="btn btn--outline btn--small"
                        title="Copy address"
                        onClick={() => copyAddress(buildAddressString(customer.address, customer.city, customer.state, customer.zip))}
                        style={{ padding: '2px 7px', fontSize: '12px' }}
                      >📋</button>
                      <button
                        className="btn btn--outline btn--small"
                        title="Open in Maps"
                        onClick={() => openInMaps(buildAddressString(customer.address, customer.city, customer.state, customer.zip))}
                        style={{ padding: '2px 7px', fontSize: '12px' }}
                      >🗺️</button>
                    </span>
                  </>
                ) : '-'}
              </span>
            </div>
            {customer.notes && (
              <div className="info-item">
                <span className="info-label">Notes</span>
                <span className="info-value">{customer.notes}</span>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card__header">
            <h3 className="card__title">Vehicles</h3>
            <button className="btn btn--outline btn--small" onClick={() => setShowVehicleModal(true)}>
              + Add Vehicle
            </button>
          </div>
          {customer.vehicles?.length === 0 ? (
            <p className="text-gray">No vehicles on file</p>
          ) : (
            <div className="vehicle-list">
              {customer.vehicles?.map((vehicle) => (
                <div key={vehicle.id} className="vehicle-item">
                  <div className="vehicle-info">
                    <span className="vehicle-name">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </span>
                    <span className="vehicle-details">
                      {vehicle.color} • {vehicle.vehicle_type}
                      {vehicle.license_plate && ` • ${vehicle.license_plate}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Saved Addresses Section */}
      <div className="card mt-lg">
        <div className="card__header">
          <h3 className="card__title">Saved Addresses</h3>
          <button className="btn btn--outline btn--small" onClick={() => {
            setEditingAddress(null);
            setAddressForm({ name: '', address: '', city: '', state: 'TX', zip: '', is_default: false });
            setShowAddressModal(true);
          }}>
            + Add Address
          </button>
        </div>
        {customer.addresses?.length === 0 ? (
          <p className="text-gray">No saved addresses. Add addresses for quick selection when creating invoices.</p>
        ) : (
          <div className="address-list">
            {customer.addresses?.map((addr) => (
              <div key={addr.id} className="address-item">
                <div className="address-info">
                  <span className="address-name">
                    {addr.name}
                    {addr.is_default === 1 && <span className="badge badge--primary ml-sm">Default</span>}
                  </span>
                  <span className="address-details">
                    {addr.address}{addr.city && `, ${addr.city}`}{addr.state && `, ${addr.state}`} {addr.zip}
                  </span>
                </div>
                <div className="address-actions">
                  <button
                    className="btn btn--outline btn--small"
                    title="Copy address"
                    onClick={() => copyAddress(buildAddressString(addr.address, addr.city, addr.state, addr.zip))}
                  >📋 Copy</button>
                  <button
                    className="btn btn--outline btn--small"
                    title="Open in Maps"
                    onClick={() => openInMaps(buildAddressString(addr.address, addr.city, addr.state, addr.zip))}
                  >🗺️ Maps</button>
                  {addr.is_default !== 1 && (
                    <button
                      className="btn btn--outline btn--small"
                      onClick={() => handleSetDefaultAddress(addr.id)}
                    >
                      Set Default
                    </button>
                  )}
                  <button
                    className="btn btn--secondary btn--small"
                    onClick={() => handleEditAddress(addr)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn--danger btn--small"
                    onClick={() => handleDeleteAddress(addr.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card mt-lg">
        <h3 className="card__title">All Invoices</h3>
        {invoices.length === 0 ? (
          <p className="text-gray">No invoices yet</p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className="clickable-row"
                    onClick={() => navigate(`/invoices?highlight=${invoice.id}`)}
                  >
                    <td className="invoice-number">{invoice.invoice_number}</td>
                    <td>{new Date(invoice.created_at).toLocaleDateString()}</td>
                    <td className="invoice-amount">${parseFloat(invoice.total).toFixed(2)}</td>
                    <td>
                      <span className={`badge badge--${invoice.status}`}>
                        {invoice.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Referrals Section */}
      <div className="card mt-lg">
        <div className="card__header">
          <h3 className="card__title">Referrals</h3>
          <button className="btn btn--outline btn--small" onClick={() => setShowAddReferral(!showAddReferral)}>
            + Add Referral
          </button>
        </div>
        {showAddReferral && (
          <div className="form-group" style={{ marginBottom: '0.75rem' }}>
            <CustomerSearchSelect
              customers={allCustomers.filter(c => c.id !== Number(id))}
              value=""
              onChange={(val) => { if (val) handleAddReferral(val); }}
              placeholder="Type who they referred..."
              required={false}
            />
          </div>
        )}
        {customer.referred_by && (
          <p className="text-gray" style={{ marginBottom: '0.75rem' }}>
            Referred by{' '}
            <span
              className="customer-name-link"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/customers/${customer.referred_by.id}`)}
            >
              {customer.referred_by.first_name} {customer.referred_by.last_name}
            </span>
          </p>
        )}
        {(() => {
          const booked = customer.referrals?.filter(r => r.appointment_count > 0).length || 0;
          return (
            <p className={booked >= 4 ? 'text-success' : 'text-gray'} style={{ fontWeight: booked >= 4 ? 700 : 500, marginBottom: '0.75rem' }}>
              {booked >= 4 ? '🎁 Eligible for a free detail!' : `${booked} of 4 referrals booked`}
            </p>
          );
        })()}
        {customer.referrals?.length === 0 || !customer.referrals ? (
          <p className="text-gray">No referrals yet</p>
        ) : (
          <div className="vehicle-list">
            {customer.referrals.map((r) => (
              <div
                key={r.id}
                className="vehicle-item"
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}
                onClick={() => navigate(`/customers/${r.id}`)}
              >
                <span className="vehicle-name">{r.first_name} {r.last_name}</span>
                <span className={`badge badge--${r.appointment_count > 0 ? 'paid' : 'draft'}`}>
                  {r.appointment_count > 0 ? 'Booked' : 'Not booked yet'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Vehicle Modal */}
      <Modal
        isOpen={showVehicleModal}
        onClose={() => setShowVehicleModal(false)}
        title="Add Vehicle"
      >
        <form onSubmit={handleAddVehicle}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Year</label>
              <input
                type="number"
                className="form-input"
                value={vehicleForm.year}
                onChange={(e) => setVehicleForm({ ...vehicleForm, year: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Make *</label>
              <input
                type="text"
                className="form-input"
                value={vehicleForm.make}
                onChange={(e) => setVehicleForm({ ...vehicleForm, make: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Model *</label>
              <input
                type="text"
                className="form-input"
                value={vehicleForm.model}
                onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Color</label>
              <input
                type="text"
                className="form-input"
                value={vehicleForm.color}
                onChange={(e) => setVehicleForm({ ...vehicleForm, color: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">License Plate</label>
              <input
                type="text"
                className="form-input"
                value={vehicleForm.license_plate}
                onChange={(e) => setVehicleForm({ ...vehicleForm, license_plate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Vehicle Type</label>
              <select
                className="form-select"
                value={vehicleForm.vehicle_type}
                onChange={(e) => setVehicleForm({ ...vehicleForm, vehicle_type: e.target.value })}
              >
                <option value="car">Car</option>
                <option value="truck">Truck/SUV</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">VIN</label>
            <input
              type="text"
              className="form-input"
              value={vehicleForm.vin}
              onChange={(e) => setVehicleForm({ ...vehicleForm, vin: e.target.value })}
            />
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => setShowVehicleModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">
              Add Vehicle
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Customer Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Customer"
      >
        <form onSubmit={handleUpdateCustomer}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input
                type="text"
                className="form-input"
                value={editForm.first_name || ''}
                onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input
                type="text"
                className="form-input"
                value={editForm.last_name || ''}
                onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone *</label>
              <input
                type="tel"
                className="form-input"
                placeholder="XXX-XXX-XXXX"
                value={editForm.phone || ''}
                onChange={(e) => setEditForm({ ...editForm, phone: formatPhoneNumber(e.target.value) })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <input
              type="text"
              className="form-input"
              value={editForm.address || ''}
              onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
            />
          </div>

          <div className="form-row form-row--3">
            <div className="form-group">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-input"
                value={editForm.city || ''}
                onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input
                type="text"
                className="form-input"
                value={editForm.state || ''}
                onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">ZIP</label>
              <input
                type="text"
                className="form-input"
                value={editForm.zip || ''}
                onChange={(e) => setEditForm({ ...editForm, zip: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              value={editForm.notes || ''}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Contact Preference</label>
            <select
              className="form-select"
              value={editForm.contact_preference || 'both'}
              onChange={(e) => setEditForm({ ...editForm, contact_preference: e.target.value })}
            >
              <option value="both">SMS & Email</option>
              <option value="sms">SMS Only</option>
              <option value="email">Email Only</option>
              <option value="none">Do Not Contact</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Referred By</label>
            <CustomerSearchSelect
              customers={allCustomers.filter(c => c.id !== Number(id))}
              value={editForm.referred_by_customer_id}
              onChange={(val) => setEditForm({ ...editForm, referred_by_customer_id: val })}
              placeholder="Search for who referred them (optional)..."
              required={false}
            />
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">
              Save Changes
            </button>
          </div>
        </form>
      </Modal>

      {/* Add/Edit Address Modal */}
      <Modal
        isOpen={showAddressModal}
        onClose={() => {
          setShowAddressModal(false);
          setEditingAddress(null);
          setAddressForm({ name: '', address: '', city: '', state: 'TX', zip: '', is_default: false });
        }}
        title={editingAddress ? "Edit Address" : "Add Address"}
      >
        <form onSubmit={handleAddAddress}>
          <div className="form-group">
            <label className="form-label">Address Name *</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., Home, Work, Lake House"
              value={addressForm.name}
              onChange={(e) => setAddressForm({ ...addressForm, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Street Address *</label>
            <input
              type="text"
              className="form-input"
              value={addressForm.address}
              onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
              required
            />
          </div>

          <div className="form-row form-row--3">
            <div className="form-group">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-input"
                value={addressForm.city}
                onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input
                type="text"
                className="form-input"
                value={addressForm.state}
                onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                maxLength="2"
              />
            </div>
            <div className="form-group">
              <label className="form-label">ZIP</label>
              <input
                type="text"
                className="form-input"
                value={addressForm.zip}
                onChange={(e) => setAddressForm({ ...addressForm, zip: e.target.value })}
                maxLength="10"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={addressForm.is_default}
                onChange={(e) => setAddressForm({ ...addressForm, is_default: e.target.checked })}
              />
              <span>Set as default address</span>
            </label>
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => {
              setShowAddressModal(false);
              setEditingAddress(null);
              setAddressForm({ name: '', address: '', city: '', state: 'TX', zip: '', is_default: false });
            }}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">
              {editingAddress ? 'Save Changes' : 'Add Address'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CustomerDetail;
